import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type {
  GenerateReportRequest,
  ReportFormat,
  ReportTypeOption,
} from '../../../../domain/src/lib/reports/reports.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

const FALLBACK_REPORT_TYPES: ReportTypeOption[] = [
  { value: 'audit_summary', label: 'Resumen de auditoría' },
  { value: 'retention_overview', label: 'Resumen de retención' },
  { value: 'repository_usage', label: 'Uso de repositorio' },
  { value: 'ingest_activity', label: 'Actividad de ingesta' },
];

@Injectable({ providedIn: 'root' })
export class ReportsService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/reports';

  /** Tipos disponibles: intenta `GET /api/reports/types` y cae a lista local si no existe. */
  getReportTypes(): Observable<ReportTypeOption[]> {
    const tenantId = resolveTenantId(this.serverContext);
    const params = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/types`, {
        headers: tenantHeaders(tenantId),
        ...(params ? { params } : {}),
      })
      .pipe(
        map((body) => this.parseTypes(body)),
        catchError(() => of(FALLBACK_REPORT_TYPES)),
      );
  }

  /**
   * Genera un archivo de informe. Respuesta binaria según `Content-Disposition` / tipo MIME.
   */
  generate(body: GenerateReportRequest): Observable<Blob> {
    const tenantId = resolveTenantId(this.serverContext);
    const params = withTenantParam(tenantId);
    return this.http.post(`${this.basePath}/generate`, body, {
      headers: tenantHeaders(tenantId),
      ...(params ? { params } : {}),
      responseType: 'blob',
    });
  }

  /** Sugerencia de extensión por formato solicitado. */
  extensionForFormat(f: ReportFormat): string {
    if (f === 'csv') return 'csv';
    if (f === 'pdf') return 'pdf';
    return 'xlsx';
  }

  private parseTypes(body: unknown): ReportTypeOption[] {
    if (!body || typeof body !== 'object') return FALLBACK_REPORT_TYPES;
    const rec = body as Record<string, unknown>;
    const items = rec['types'] ?? rec['items'] ?? rec['data'];
    if (!Array.isArray(items)) return FALLBACK_REPORT_TYPES;
    const out: ReportTypeOption[] = [];
    for (const it of items) {
      if (it && typeof it === 'object') {
        const o = it as Record<string, unknown>;
        const value = String(o['value'] ?? o['id'] ?? o['type'] ?? '');
        const label = String(o['label'] ?? o['name'] ?? value);
        if (value) out.push({ value, label });
      }
    }
    return out.length ? out : FALLBACK_REPORT_TYPES;
  }
}
